import { PlusOutlined } from "@ant-design/icons"
import { Button, Divider, Select, type SelectProps } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import { useMemo } from "react"

import { type ProjectChecklistFilterOptions } from "../../../../../../redux/project.types.ts"

interface ProjectViewChecklistsAssignedSelectProps extends Omit<
  SelectProps,
  "options"
> {
  options: ProjectChecklistFilterOptions
  spreedOption?: boolean
  onAddClick?: () => void
}

const KEY_SEPARATOR = "___"

export function ProjectViewChecklistsAssignedSelect({
  onAddClick,
  onChange,
  value,
  placeholder = "Select assigned",
  className,
  options,
  spreedOption,
  ...props
}: ProjectViewChecklistsAssignedSelectProps) {
  const assignedToOptions: BaseOptionType[] = useMemo(() => {
    const manages = options?.project_managers ?? []
    const admins = options?.admins ?? []
    const subcontractors = options?.subcontractors ?? []

    const list = []

    if (manages.length) {
      list.push({
        label: "Project Managers",
        title: "project_managers",
        options: manages.map((m, index) => ({
          ...(spreedOption ? m : {}),
          label: m.label || "No name found",
          value: `pm${KEY_SEPARATOR}${index}${KEY_SEPARATOR}${m.value}`
        }))
      })
    }

    if (admins.length) {
      list.push({
        label: "Admins",
        title: "admins",
        options: admins.map((a, index) => ({
          ...(spreedOption ? a : {}),
          label: a.label || "No name found",
          value: `admin${KEY_SEPARATOR}${index}${KEY_SEPARATOR}${a.value}`
        }))
      })
    }

    if (subcontractors.length) {
      list.push({
        label: "Subcontractors",
        title: "subcontractors",
        options: subcontractors.map((s, index) => ({
          ...(spreedOption ? s : {}),
          label: s?.label || "No name found",
          value: `sub${KEY_SEPARATOR}${index}${KEY_SEPARATOR}${s?.value?.contact_user}`
        }))
      })
    }

    return list
  }, [options, spreedOption])

  // Витягуємо чистий value без префіксу
  const extractCleanValue = (val: string | undefined) => {
    if (!val || !val.includes(KEY_SEPARATOR)) return val
    const parts = val.split(KEY_SEPARATOR)
    return parts[parts.length - 1]
  }

  // Конвертуємо зовнішній value в внутрішній формат для Select
  const internalValue = useMemo(() => {
    if (!value) return value

    // Шукаємо опцію з таким чистим value
    for (const group of assignedToOptions) {
      const found = group.options?.find(
        (opt: BaseOptionType) => extractCleanValue(opt.value) === value
      )
      if (found) return found.value
    }

    return value
  }, [value, assignedToOptions])

  const handleChange: SelectProps["onChange"] = (val, option) => {
    const cleanValue = extractCleanValue(val as string)
    onChange?.(cleanValue, option)
  }

  return (
    <Select
      options={assignedToOptions}
      placeholder={placeholder}
      className={className}
      allowClear
      value={internalValue}
      onChange={handleChange}
      showSearch
      filterOption={(input, option) =>
        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
      }
      popupRender={
        onAddClick
          ? (menu) => (
              <>
                {menu}
                <Divider size={"small"} />
                <Button
                  className={"w-full"}
                  onClick={onAddClick}
                  icon={<PlusOutlined />}
                >
                  New Subcontractor
                </Button>
              </>
            )
          : undefined
      }
      {...props}
    />
  )
}
